import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  UseQueryOptions,
  UseQueryResult,
  QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import type { Fish } from '../types/fish';
import { stableStringify } from '@Utils/searchParams';

type TeamMembersResponse = { data: Fish[]; meta: Record<string, any> };
type DefaultError = unknown;

type UseTeamMembersParams<S extends Record<string, any> = Record<string, any>> =
  {
    /** Optional query string params to forward to service.readAllFish */
    searchParams?: S;
    /** Override default enabled behavior */
    enabled?: boolean;
  };

/**
 * useMembers
 *
 * @example
  const venueQuery = useTeamMembers(
    {
      venueId,
      searchParams: {
        include: 'fishProfiles',
        }
     },
    { staleTime: 60_000 }
  );
 */
export function useTeamMembers<
  TData = TeamMembersResponse,
  TError = DefaultError,
>(
  { searchParams, enabled = true }: UseTeamMembersParams = {},
  options?: Omit<
    UseQueryOptions<TeamMembersResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const stableParams = useMemo(
    () => stableStringify(searchParams),
    [searchParams],
  );

  return useQuery<TeamMembersResponse, TError, TData>({
    queryKey: ['fish', stableParams],
    enabled: enabled,
    queryFn: async () => {
      return service.getMembersWithMeta(searchParams ?? {});
    },
    // caller can pass select, staleTime, gcTime, retry, refetchOnWindowFocus, etc.
    ...(options as any),
  });
}

export function useActiveTeamMembers<
  TData = TeamMembersResponse,
  TError = DefaultError,
>(
  { searchParams, enabled = true }: UseTeamMembersParams = {},
  options?: Omit<
    UseQueryOptions<TeamMembersResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const params = Object.assign({}, searchParams);

  params['filter[fishProfiles][some][employee_status]'] = 'ACTIVE';
  params['filter[fishProfiles][some][profileType]'] = 'EMPLOYEE';
  const stableParams = useMemo(() => stableStringify(params), [params]);

  return useQuery<TeamMembersResponse, TError, TData>({
    queryKey: ['fish', stableParams],
    enabled: enabled,
    queryFn: async () => {
      return service.getMembersWithMeta(params ?? {});
    },
    // caller can pass select, staleTime, gcTime, retry, refetchOnWindowFocus, etc.
    ...(options as any),
  });
}
